import { useEffect, useRef, useState } from 'react';

import { ExternalLink } from 'lucide-react';

import { Button } from '@frontend/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@frontend/components/ui/card';
import { Input } from '@frontend/components/ui/input';
import { setLocalToken } from '@frontend/features/auth/util/util.auth.localToken';
import {
  isTokenExpired,
  parseJwt,
  validateTokenClaims,
} from '@frontend/features/auth/util/util.auth.parseJwt';

export function TokenExchangePage() {
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenDevEnvironment = () => {
    window.open(
      'https://dev.network.lts.com/copy-dev-token',
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleLogin = (tokenValue?: string) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use provided token or fall back to state
      const tokenToValidate = tokenValue || token;

      if (!tokenToValidate || tokenToValidate.trim() === '') {
        throw new Error('Please paste a token');
      }

      // Validate token format
      const claims = parseJwt(tokenToValidate.trim());

      // Check if token has required claims
      if (!validateTokenClaims(claims)) {
        throw new Error(
          'Token is missing required claims (oid, email/upn, name)',
        );
      }

      // Check if token is expired
      if (isTokenExpired(claims)) {
        throw new Error(
          'Token has expired. Please copy a fresh token from dev environment.',
        );
      }

      // Store token
      setLocalToken(tokenToValidate.trim());

      // Show success message
      setSuccess('Token successfully stored! Redirecting to application...');

      // Reload page to trigger AuthGuard to detect the token
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (err) {
      console.error('Failed to validate token:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to validate token. Please check the token format.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-focus input on mount and when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        inputRef.current?.focus();
      }
    };

    // Focus on mount
    inputRef.current?.focus();

    // Focus when tab becomes active
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Card className="w-[450px]">
        <CardHeader>
          <CardTitle>Localhost Development Mode</CardTitle>
          <CardDescription>
            Follow these steps to authenticate with your dev environment token
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
              {success}
            </div>
          )}

          {/* Step 1: Open Dev Environment */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Step 1: Get Token</div>
            <Button
              onClick={handleOpenDevEnvironment}
              className="w-full"
              variant="outline"
              disabled={Boolean(success)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Dev Environment
            </Button>
          </div>

          {/* Step 2: Paste Token */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Step 2: Paste Token</div>
            <Input
              ref={inputRef}
              type="text"
              placeholder="Paste your token here..."
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={Boolean(success)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // Prevent form submission
                  if (token.trim()) {
                    // Existing behavior: log in with token
                    handleLogin();
                  } else {
                    // New behavior: open copy page when empty
                    handleOpenDevEnvironment();
                  }
                }
              }}
              onPaste={(e) => {
                // Get pasted text from clipboard
                const pastedText = e.clipboardData.getData('text');

                if (pastedText && pastedText.trim()) {
                  // Update token state for display
                  setToken(pastedText.trim());

                  // Automatically trigger login with the pasted token
                  // Pass token directly to avoid state timing issues
                  setTimeout(() => {
                    handleLogin(pastedText.trim());
                  }, 0);
                }
              }}
            />
            <Button
              onClick={() => handleLogin()}
              className="w-full"
              disabled={isLoading || !token.trim() || Boolean(success)}
            >
              {isLoading ? 'Validating...' : 'Log In'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
